import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { User, LogOut, Settings } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import SCCNLogo from "../assets/images/sccn-logo.svg";
import GTFLogo from "../assets/images/gtf-logo.svg";

const Navbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeNestedDropdown, setActiveNestedDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, userType, logout } = useAuth();

  // Helper function to check if nav item is active
  const isNavItemActive = (item) => {
    const currentPath = location.pathname;
    
    // Direct path match
    if (item.path === currentPath) return true;
    
    // Check if any subItem path matches
    if (item.subItems) {
      return item.subItems.some(subItem => {
        if (subItem.path === currentPath) return true;
        // Check nested subitems
        if (subItem.subItems) {
          return subItem.subItems.some(nested => nested.path === currentPath);
        }
        return false;
      });
    }
    
    return false;
  };

  const navItems = [
    {
      name: "GTF",
      path: "/about",
      subItems: [
        { name: "Who We Are", path: "/about" },
        {
          name: "Founder Directors",
          path: "/about/founder-directors",
          subItems: [
            { name: "Dr. Rajendra Shende", path: "/about/founder-directors/rajendra-shende" },
            { name: "Mr. Sandeep Narang", path: "/about/founder-directors/sandeep-narang" },
          ],
        },
        { name: "Mentors", path: "/sccn/mentors" },
        { name: "Advisors", path: "/sccn/advisors" },
        { name: "Our Associates", path: "/about/associates" },
      ],
    },
    {
      name: "SCCN",
      path: "/sccn",
      subItems: [
        { name: "About SCCN", path: "/sccn" },
        { name: "Experts", path: "/sccn/experts" },
        { name: "Team", path: "/sccn/team" },
      ],
    },
    {
      name: "Initiatives",
      path: "/initiatives",
      subItems: [
        { name: "University-Determined Contributions (UDCs)", path: "/initiatives/udcs" },
        { name: "U75 Net-Zero Campus Movement", path: "/initiatives/u75-consortium" },
        { name: "Net Zero Campus Initiative", path: "/initiatives/net-zero" },
      ],
    },
    {
      name: "Events",
      path: "#",
      subItems: [
        { name: "Event Calendar", path: "/events/calendar" },
        { name: "Past Events", path: "/events/past" },
      ],
    },
    {
      name: "Media & Publications",
      path: "#",
      subItems: [
        { name: "Blogs", path: "/blogs" },
        { name: "Press Releases", path: "/media/press-releases" },
        { name: "Simply Smart", path: "/simply-smart" },
        { name: "Globally Smart", path: "/globally-smart" },
        { name: "GTF in Spotlight", path: "/media/gtf-spotlight" },
        { name: "Kaas Annual Report Card", path: "/media/kaas-annual-report-card" },
      ],
    },
    {
      name: "Action Network",
      path: "#",
      subItems: [
        { name: "Registered Campuses", path: "/action-network/campuses" },
        { name: "Regional Hubs", path: "/action-network/regional-hubs" },
        { name: "Success Stories", path: "/action-network/success-stories" },
        // { name: "Youth Ambassadors", path: "/action-network/youth-ambassadors" },
      ],
    },
    { name: "Resources", path: "/resources" },
    {
      name: "Career",
      path: "#",
      subItems: [
        { name: "SCCN Careers", path: "/careers" },
        { name: "Green Careers", path: "/green-careers" },
      ],
    },
    // Only show Register menu if not authenticated
    ...(!isAuthenticated() ? [{
      name: "Register",
      path: "#",
      subItems: [
        { name: "Youth Ambassador", path: "/student-registration" },
        { name: "Campus Registration", path: "/campus-registration" },
        { name: "Schools Registration", path: "https://forms.gle/vNJVKugow9Z5uRYA9" },
      ],
    }] : []),
  ];

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const DropdownArrow = ({ isOpen }) => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      className={`ml-1 transition-transform duration-200 ease-in-out ${isOpen ? 'rotate-180' : ''} stroke-black`}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6,9 12,15 18,9"></polyline>
    </svg>
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container") && !event.target.closest(".user-menu-container")) {
        setActiveDropdown(null);
        setShowUserMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLogoClick = () => {
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavItemClick = (path) => {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else if (path.startsWith("/#")) {
      // Hash navigation on homepage
      const sectionId = path.substring(2);
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else if (path.includes("#")) {
      // Hash navigation on other pages (e.g., /initiatives#sccn)
      navigate(path); // Navigate with hash, component will handle scroll
    } else {
      navigate(path);
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const handleDropdownToggle = (dropdownName, event) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* Inline styles for cross-browser compatibility */}
      <style>{`
        .transform-3d { transform: translateZ(0); -webkit-transform: translateZ(0); }
        .backface-hidden { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
      `}</style>

      <div className={`fixed top-0 left-0 right-0 z-50`}>
        <nav 
          className={`
            flex items-center justify-center h-16 w-full 
            px-4 sm:px-6 lg:px-8 border-b border-gray-200 transition-all duration-300 transform-3d
            bg-white
          `}
        >
          <div className="mx-auto max-w-7xl w-full flex items-center justify-between">
            {/* Logos */}
            <div
              className="flex items-center gap-2 cursor-pointer flex-shrink-0"
              onClick={handleLogoClick}
            >
              <img
                src={GTFLogo}
                alt="GTF Logo"
                className="h-8 md:h-9 lg:h-10 w-auto object-contain transition-all duration-300"
              />
              {/* Vertical Separator */}
              <div className="h-6 md:h-7 lg:h-8 w-px bg-gray-300 transition-all duration-300"></div>
              <img
                src={SCCNLogo}
                alt="SCCN Logo"
                className="h-8 md:h-9 lg:h-10 w-auto object-contain transition-all duration-300"
              />
            </div>

            {/* Desktop Navigation - Visible from 1280px (xl) and up for proper iPad support */}
            <div className="hidden xl:flex items-center gap-1">
              {navItems.map((item) => (
                <div
                  key={item.name}
                  className="relative dropdown-container"
                >
                  {item.subItems ? (
                    <>
                      <div className={`rounded-lg transition-all duration-300 hover:bg-black/5 ${isNavItemActive(item) ? 'bg-emerald-50 border-b-2 border-emerald-600' : ''}`}>
                        <button
                          className={`
                            flex items-center font-semibold whitespace-nowrap transition-all duration-300
                            px-2 py-2 xl:px-3 min-h-[2rem] xl:min-h-[2.5rem]
                            text-xs xl:text-sm 2xl:text-base
                            ${isNavItemActive(item) ? 'text-emerald-700' : 'text-black'}
                            bg-transparent border-0 outline-none focus:outline-none active:outline-none
                          `}
                          onClick={(e) => handleDropdownToggle(item.name, e)}
                        >
                          {item.name}
                          <DropdownArrow isOpen={activeDropdown === item.name} />
                        </button>
                      </div>
                      <AnimatePresence>
                        {activeDropdown === item.name && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="
                              absolute left-0 mt-2 rounded-xl py-2 z-[60]
                              bg-white backdrop-blur-xl border border-gray-200 shadow-lg
                              min-w-[14rem] lg:min-w-[16rem] xl:min-w-[18rem]
                              max-w-[calc(100vw-2rem)] transform-3d backface-hidden
                            "
                          >
                            {item.subItems.map((subItem) => (
                              subItem.subItems ? (
                                <div
                                  key={subItem.name}
                                  className="relative nested-dropdown"
                                  onMouseEnter={() => setActiveNestedDropdown(subItem.name)}
                                  onMouseLeave={() => setActiveNestedDropdown(null)}
                                >
                                  <button
                                    onClick={() => subItem.path !== "#" && handleNavItemClick(subItem.path)}
                                    className={`
                                      w-full text-left text-gray-800 font-semibold rounded-lg 
                                      flex items-center justify-between transition-all duration-200
                                      hover:bg-black/5 min-h-[2rem] lg:min-h-[2.5rem]
                                      px-3 py-2 lg:px-4 lg:py-3
                                      text-xs lg:text-sm xl:text-base
                                    `}
                                  >
                                    {subItem.name}
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="ml-2 flex-shrink-0"
                                    >
                                      <polyline points="9,18 15,12 9,6"></polyline>
                                    </svg>
                                  </button>
                                  {activeNestedDropdown === subItem.name && (
                                    <motion.div
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      exit={{ opacity: 0, x: -10 }}
                                      transition={{ duration: 0.2 }}
                                      className="
                                        absolute left-full top-0 ml-2 rounded-xl py-2 z-[60]
                                        bg-white backdrop-blur-xl border border-gray-200 shadow-lg
                                        min-w-[12rem] lg:min-w-[15rem]
                                        max-w-[calc(100vw-2rem)] transform-3d backface-hidden
                                      "
                                    >
                                      {subItem.subItems.map((nestedItem) => (
                                        <button
                                          key={nestedItem.name}
                                          onClick={() => handleNavItemClick(nestedItem.path)}
                                          className={`
                                            w-full text-left text-gray-800 font-semibold rounded-lg
                                            transition-all duration-200 hover:bg-black/5
                                            min-h-[2rem] lg:min-h-[2.5rem]
                                            px-3 py-2 lg:px-4 lg:py-3
                                            text-xs lg:text-sm xl:text-base
                                          `}
                                        >
                                          {nestedItem.name}
                                        </button>
                                      ))}
                                    </motion.div>
                                  )}
                                </div>
                              ) : (
                                <button
                                  key={subItem.name}
                                  onClick={() => handleNavItemClick(subItem.path)}
                                  className={`
                                    w-full text-left text-gray-800 font-semibold rounded-lg
                                    transition-all duration-200 hover:bg-black/5
                                    min-h-[2rem] lg:min-h-[2.5rem]
                                    px-3 py-2 lg:px-4 lg:py-3
                                    text-xs lg:text-sm xl:text-base
                                  `}
                                >
                                  {subItem.name}
                                </button>
                              )
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <div className={`rounded-lg transition-all duration-300 hover:bg-black/5 ${isNavItemActive(item) ? 'bg-emerald-50 border-b-2 border-emerald-600' : ''}`}>
                      <button
                        onClick={() => handleNavItemClick(item.path)}
                        className={`
                          font-semibold whitespace-nowrap transition-all duration-300
                          px-2 py-2 xl:px-3 min-h-[2rem] xl:min-h-[2.5rem]
                          text-xs xl:text-sm 2xl:text-base
                          ${isNavItemActive(item) ? 'text-emerald-700' : 'text-black'}
                          bg-transparent border-0 outline-none focus:outline-none active:outline-none
                        `}
                      >
                        {item.name}
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* Contact Link + User Menu - Desktop */}
              <div className="flex items-center gap-2">
                <div className="rounded-lg transition-all duration-300 hover:bg-black/5">
                  <button
                    onClick={() => handleNavItemClick('/contact')}
                    className="
                      font-semibold whitespace-nowrap transition-all duration-300
                      px-2 py-2 xl:px-3 min-h-[2rem] xl:min-h-[2.5rem]
                      text-xs xl:text-sm 2xl:text-base
                      text-black hover:text-emerald-700
                      bg-transparent border-0 outline-none focus:outline-none active:outline-none
                    "
                  >
                    Contact
                  </button>
                </div>

                {/* User Profile Menu or Sign In Button */}
                {isAuthenticated() ? (
                  <div className="relative user-menu-container">
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="
                        flex items-center gap-2 px-4 py-2 rounded-lg
                        bg-emerald-50 border border-emerald-200
                        hover:bg-emerald-100 transition-all duration-300
                      "
                    >
                      <User className="w-4 h-4 text-emerald-700" />
                      <span className="text-sm font-medium text-emerald-700 max-w-[100px] truncate">
                        {user?.name || 'Profile'}
                      </span>
                    </button>

                    {showUserMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="
                          absolute right-0 mt-2 w-56 rounded-xl py-2 z-[60]
                          bg-white backdrop-blur-xl border border-gray-200 shadow-lg
                        "
                      >
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                          <p className="text-xs text-emerald-600 mt-1 capitalize">{userType} Account</p>
                        </div>
                        
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate(`/${userType}/dashboard`);
                          }}
                          className="
                            w-full text-left px-4 py-2.5 text-sm
                            hover:bg-gray-50 transition-colors
                            flex items-center gap-2 text-gray-700
                          "
                        >
                          <Settings className="w-4 h-4" />
                          Dashboard
                        </button>

                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate(`/${userType}/profile`);
                          }}
                          className="
                            w-full text-left px-4 py-2.5 text-sm
                            hover:bg-gray-50 transition-colors
                            flex items-center gap-2 text-gray-700
                          "
                        >
                          <User className="w-4 h-4" />
                          View Profile
                        </button>

                        <div className="border-t border-gray-100 mt-1"></div>
                        
                        <button
                          onClick={handleLogout}
                          className="
                            w-full text-left px-4 py-2.5 text-sm
                            hover:bg-red-50 transition-colors
                            flex items-center gap-2 text-red-600
                          "
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavItemClick('/signin')}
                    className="
                      text-white font-semibold whitespace-nowrap rounded-lg flex-shrink-0
                      transition-all duration-300 transform hover:-translate-y-0.5
                      min-h-[2rem] xl:min-h-[2.5rem]
                      px-4 xl:px-6 py-2
                      text-xs xl:text-sm 2xl:text-base
                      bg-[#035925] border border-transparent hover:bg-[#024a1e]
                    "
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Menu Button - Visible below 1280px (xl) for iPad support */}
            <button
              onClick={toggleMobileMenu}
              className="xl:hidden focus:outline-none ml-auto transition-colors duration-300 text-black"
              aria-label="Toggle menu"
            >
              <svg
                className="h-7 w-7 md:h-8 md:w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </nav>

        {/* Mobile Menu - Below 1280px for iPad support */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="
                xl:hidden overflow-hidden z-[45]
                bg-white backdrop-blur-xl border-t border-gray-200 shadow-xl
                max-h-[calc(100vh-80px)] overflow-y-auto transform-3d backface-hidden
              "
            >
                <div className="px-4 py-2">
                  {navItems.map((item) => (
                    <div key={item.name} className="mb-1">
                      {item.subItems ? (
                        <div className="relative">
                          <button
                            className={`
                              flex items-center justify-between w-full font-semibold
                              transition-all duration-200 hover:bg-black/5
                              text-black rounded-lg mb-1
                              px-4 py-3 text-sm md:text-base min-h-[2.5rem]
                            `}
                            onClick={(e) => handleDropdownToggle(item.name, e)}
                          >
                            {item.name}
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              className="stroke-black transition-transform duration-200 ease-in-out"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                transform: activeDropdown === item.name ? "rotate(180deg)" : "rotate(0deg)",
                              }}
                            >
                              <polyline points="6,9 12,15 18,9"></polyline>
                            </svg>
                          </button>
                          {activeDropdown === item.name && (
                            <div className="pl-4 mt-1 space-y-1">
                              {item.subItems.map((subItem) => (
                                subItem.subItems ? (
                                  <div key={subItem.name} className="relative">
                                    <button
                                      className={`
                                        w-full text-left font-semibold flex items-center justify-between
                                        transition-all duration-200 hover:bg-black/5
                                        text-black rounded-lg mb-1
                                        px-4 py-2.5 text-xs md:text-sm min-h-[2rem]
                                      `}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveNestedDropdown(activeNestedDropdown === subItem.name ? null : subItem.name);
                                      }}
                                    >
                                      {subItem.name}
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        className="stroke-black transition-transform duration-200 ease-in-out"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{
                                          transform: activeNestedDropdown === subItem.name ? "rotate(90deg)" : "rotate(0deg)",
                                        }}
                                      >
                                        <polyline points="9,18 15,12 9,6"></polyline>
                                      </svg>
                                    </button>
                                    {activeNestedDropdown === subItem.name && (
                                      <div className="pl-4 mt-1 space-y-1">
                                        {subItem.subItems.map((nestedItem) => (
                                          <button
                                            key={nestedItem.name}
                                            onClick={() => handleNavItemClick(nestedItem.path)}
                                            className={`
                                              w-full text-left font-semibold
                                              transition-all duration-200 hover:bg-black/5
                                              text-black rounded-lg mb-1
                                              px-4 py-2 text-xs min-h-[1.75rem]
                                            `}
                                          >
                                            {nestedItem.name}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    key={subItem.name}
                                    onClick={() => handleNavItemClick(subItem.path)}
                                    className={`
                                      w-full text-left font-semibold
                                      transition-all duration-200 hover:bg-black/5
                                      text-black rounded-lg mb-1
                                      px-4 py-2.5 text-xs md:text-sm min-h-[2rem]
                                    `}
                                  >
                                    {subItem.name}
                                  </button>
                                )
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleNavItemClick(item.path)}
                          className={`
                            font-semibold w-full text-left
                            transition-all duration-200 hover:bg-black/5
                            text-black rounded-lg mb-1
                            px-4 py-3 text-sm md:text-base min-h-[2.5rem]
                          `}
                        >
                          {item.name}
                        </button>
                      )}
                    </div>
                  ))}
                  
                  {/* Contact Button - Mobile */}
                  <button
                    onClick={() => handleNavItemClick('/contact')}
                    className={`
                      font-semibold w-full text-left mt-2
                      bg-[#035925] text-white hover:bg-[#024a1e]
                      transition-all duration-200 rounded-lg
                      px-4 py-3 text-sm md:text-base min-h-[2.5rem]
                    `}
                  >
                    Contact
                  </button>

                  {/* User Menu or Sign In Button - Mobile */}
                  {isAuthenticated() ? (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="px-4 py-3 bg-emerald-50 rounded-lg mb-2">
                        <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        <p className="text-xs text-emerald-600 mt-1 capitalize">{userType} Account</p>
                      </div>
                      
                      <button
                        onClick={() => handleNavItemClick(`/${userType}/dashboard`)}
                        className="
                          w-full text-left px-4 py-2.5 mb-1
                          hover:bg-gray-50 transition-colors rounded-lg
                          flex items-center gap-2 text-gray-700 font-medium
                        "
                      >
                        <Settings className="w-4 h-4" />
                        Dashboard
                      </button>

                      <button
                        onClick={() => handleNavItemClick(`/${userType}/profile`)}
                        className="
                          w-full text-left px-4 py-2.5 mb-1
                          hover:bg-gray-50 transition-colors rounded-lg
                          flex items-center gap-2 text-gray-700 font-medium
                        "
                      >
                        <User className="w-4 h-4" />
                        View Profile
                      </button>

                      <button
                        onClick={handleLogout}
                        className="
                          w-full text-left px-4 py-2.5
                          hover:bg-red-50 transition-colors rounded-lg
                          flex items-center gap-2 text-red-600 font-medium
                        "
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNavItemClick('/signin')}
                      className="
                        font-semibold w-full text-left mt-2
                        bg-white text-emerald-700 border-2 border-emerald-600
                        hover:bg-emerald-50 transition-all duration-200 rounded-lg
                        px-4 py-3 text-sm md:text-base min-h-[2.5rem]
                      "
                    >
                      Sign In
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
      </div>
    </>
  );
};

export default Navbar;
